import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { IFlightMonitorRow } from '@/lib/flights/types';
import { SOURCE_BADGE_CLASS, SOURCE_TONE } from '@/components/provenance/source-tone';
import { formatFlightClock24, formatOperatorClock24 } from '@/lib/disruptions/airport-tz';
import type { ClockBasis } from './clock-basis';
import { disruptionEntry } from '@/routes/disruptions/disruption-entry';
import { BOARD_BADGE_TONE, rowBadge } from '@/routes/disruptions/board-model';
import { useDisruption } from '@/lib/disruptions/use-disruptions';
import {
  airlineEtd,
  boardStatusCell,
  flightRowSources,
  riskCell,
  selectDisabledReason,
  type FlightSourceHint,
} from './status';

// One departures-board row (~32px): TIME · FLIGHT · ROUTE · EQP · PAX · RISK ·
// STATUS. Numeric cells are mono/tabular; the STATUS cell is the FIDS caption
// (ON TIME / DELAYED +34m / …) and flashes briefly when a live
// `flight.status-changed` push lands (motion-safe only). Terminal rows dim.
//
// SOURCES (providers-by-service): the STATUS caption carries a compact badge
// naming where the status came from (Airline / FlightAware / File / Synthetic),
// stacked under it so it fits the narrow phone column and stays visible there.
// The RISK cell names its source in its tooltip (always the predictor). Both
// tooltips carry the observation time. The `+Nm` minutes are those of the
// status's own source: the predictor's forecast under FlightAware, the airline's
// delay under Airline; none under a file or synthetic status.
//
// AIRLINE (synthetic-airline U1): a status the airline reported wins the cell —
// on load (`provenance.status`) and live (the `flight.status-changed` delta's
// top-level `statusSource`), over FlightAware whatever the times — save FlightAware's
// actual DEPARTED / ARRIVED over an airline SCHEDULED / DELAYED; between two airline
// observations the newer (`flightRowSources`). Its DELAYED
// also shows the airline's ETD, in the departure airport's clock like TIME.
//
// Three optional interaction modes (mutually exclusive at the call sites):
//
// SELECT MODE (`selection` set — the /flights board): a leading CHECKBOX per row
// feeds the board's multi-select → "Start disruption" bar. Flights a new
// disruption could not cover (already in one, already carrying a case, already
// arrived) render a disabled box with the reason. The board does NOT open cases
// any more — a case only ever exists under a declared disruption.
//
// PICK MODE (`onSelect` set — the Express flow): the WHOLE row is the button
// (keyboard-operable), the case/disruption marks render as plain chips instead
// of away-navigating links, and the trailing action cell shows a chevron.
//
// ACTION MODE (`action` set — the universal search on «Resumen» / /passengers): the
// same row, the trailing cell widened to hold the row's ONE action («Declarar
// disrupción», «Operar disrupción»…) — PO 2026-11-01: a flight found by the search
// «debe mostrarse igual» as on this board, its status in the same place. Under `sm` the
// action takes its own line under the row.

/** Shared column template — the sticky header in flights.tsx uses the same one. */
export const BOARD_GRID =
  'grid grid-cols-[3.4rem_4.6rem_minmax(5rem,1fr)_7.4rem_2.2rem] items-center gap-x-3 ' +
  'sm:grid-cols-[3.4rem_3.4rem_4.6rem_minmax(6rem,18rem)_1fr_3.6rem_3.2rem_3.2rem_7.4rem_2.2rem]';

/** Select-mode template — BOARD_GRID plus a leading 1.4rem checkbox column.
 *  Keep the tail IN SYNC with BOARD_GRID (Tailwind needs full literals). */
export const BOARD_GRID_SELECT =
  'grid grid-cols-[1.4rem_3.4rem_4.6rem_minmax(5rem,1fr)_7.4rem_2.2rem] items-center gap-x-3 ' +
  'sm:grid-cols-[1.4rem_3.4rem_3.4rem_4.6rem_minmax(6rem,18rem)_1fr_3.6rem_3.2rem_3.2rem_7.4rem_2.2rem]';

/** Action-mode template — BOARD_GRID with the trailing cell widened to an action
 *  button (11rem); under `sm` the action wraps to its own line. Keep the head IN SYNC
 *  with BOARD_GRID (Tailwind needs full literals). */
export const BOARD_GRID_ACTION =
  'grid grid-cols-[3.4rem_4.6rem_minmax(5rem,1fr)_7.4rem] items-center gap-x-3 gap-y-1.5 ' +
  'sm:grid-cols-[3.4rem_3.4rem_4.6rem_minmax(6rem,18rem)_1fr_3.6rem_3.2rem_3.2rem_7.4rem_11rem]';

/** The column header of an action-mode list — the /flights header's cells on
 *  BOARD_GRID_ACTION (decorative: the rows carry their own labels). */
export function FlightActionColumns({ clock = 'local' }: { clock?: ClockBasis }) {
  const { t } = useTranslation('network');
  return (
    <div
      aria-hidden
      className={cn(
        BOARD_GRID_ACTION,
        'border-b border-border bg-card px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground',
      )}
      data-testid="flight-action-columns"
    >
      <span>{t(clock === 'origin' ? 'board.columns.timeOrigin' : 'board.columns.timeLocal')}</span>
      <span className="hidden sm:block">
        {t(clock === 'origin' ? 'board.columns.timeLocal' : 'board.columns.timeOrigin')}
      </span>
      <span>{t('board.columns.flight')}</span>
      <span>{t('board.columns.route')}</span>
      <span className="hidden sm:block" />
      <span className="hidden sm:block">{t('board.columns.equipment')}</span>
      <span className="hidden sm:block">{t('board.columns.pax')}</span>
      <span className="hidden sm:block" title={t('board.riskHint')}>
        {t('board.columns.risk')}
      </span>
      <span>{t('board.columns.status')}</span>
      <span className="hidden sm:block" />
    </div>
  );
}

/** Selection handlers the /flights board passes down (absent everywhere else). */
export interface FlightRowSelection {
  selectedUrns: ReadonlySet<string>;
  onToggle: (row: IFlightMonitorRow) => void;
}

// Issue #662: Removed formatTime() - flight times must be formatted in the
// departure airport's timezone. Use formatFlightClock24 from airport-tz.ts.

const DISRUPTION_CHIP_CLASS = 'min-w-0 max-w-full truncate rounded px-1.5 py-px text-[10px] font-semibold uppercase';

/**
 * The flight's disruption: its CAUSE and its STATE — the same badge the disruptions list
 * and Operar's header show («Meteorología · DEMORADO +4 h», «… · TODO RESUELTO»), PO
 * 2026-10-27: «en el botón de disrupción pongamos el motivo y el estado». One read per
 * disruption (`useDisruption`, shared cache — many flights under one disruption read it
 * once); «Disrupción» while it loads or when it cannot be read. The case chip is gone:
 * a case only ever exists under its disruption, which the chip already opens.
 */
function DisruptionChip({ urn, link }: { urn: string; link: boolean }) {
  const { t } = useTranslation('network');
  const { data } = useDisruption(urn);
  const badge = data ? rowBadge(data) : null;
  const label = data
    ? `${t(`disruptions:enums.cause.${data.cause}`, { defaultValue: data.cause })} · ${t(
        `disruptions:${badge!.key}`,
        badge!.params ?? {},
      )}`
    : t('row.disruptionChip');
  const tone = badge
    ? BOARD_BADGE_TONE[badge.tone]
    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  const className = cn(DISRUPTION_CHIP_CLASS, tone, link && 'hover:underline');
  if (!link)
    return (
      <span className={className} title={label} data-testid="flight-disruption-chip">
        {label}
      </span>
    );
  return (
    <Link
      // The chip enters the disruption on its default view.
      to={disruptionEntry(urn).to}
      state={disruptionEntry(urn).state}
      className={className}
      title={label}
      data-testid="flight-disruption-chip"
    >
      {label}
    </Link>
  );
}

// Source badge under the status caption: the shared provenance badge's shape and
// tones (`components/provenance/source-badge`), capped to the status column and
// ellipsed rather than widening the grid.

/** Observation instant for a tooltip, in UTC (the board's clock also states UTC),
 *  formatted in the UI language rather than the browser's; null when the source
 *  did not say or the value does not parse. */
function observedAtUtc(iso: string | undefined, locale: string): string | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  const when = new Date(at).toLocaleString(locale, {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${when} UTC`;
}

export function FlightRowItem({
  row,
  onSelect,
  selection,
  action,
  clock = 'local',
}: {
  row: IFlightMonitorRow;
  onSelect?: (row: IFlightMonitorRow) => void;
  selection?: FlightRowSelection;
  /** The row's one action (ACTION MODE) — rendered in the widened trailing cell. */
  action?: ReactNode;
  /** The board's clock: which time is set big, and the ETD's clock (`clock-basis.ts`). */
  clock?: ClockBasis;
}) {
  const { t, i18n } = useTranslation('network');
  const pickMode = !!onSelect;
  const selectMode = !pickMode && !!selection;
  const localTime = formatOperatorClock24(row.scheduledDepartureUtc);
  const originTime = formatFlightClock24(row.scheduledDepartureUtc, row.origin);

  const fids = boardStatusCell(row);
  const etd = airlineEtd(row);
  const risk = riskCell(row.cancelProbability, row.expectedDelayMinutes);
  const sources = flightRowSources(row);

  // "Source: Airline · observed 22 Oct, 14:05 UTC" — the tooltip half of a badge.
  const sourceTitle = (hint: FlightSourceHint): string => {
    const when = observedAtUtc(hint.observedAt, i18n.language);
    return [
      t('row.source.label', { source: t(hint.labelKey) }),
      when ? t('row.source.observedAt', { when }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  };
  const riskTitle =
    risk && sources.risk
      ? `${t('board.riskHint')} · ${sourceTitle(sources.risk)}`
      : t('board.riskHint');

  // Live-status flash: when the WS merge changes what the cell says — the status,
  // its minutes, or who reported it (the airline confirming it) — the cell pulses
  // once (split-flap feel). State-driven, no timers while idle.
  const shown =
    row.status === undefined
      ? undefined
      : `${fids.labelKey}|${fids.labelParams?.minutes ?? ''}|${sources.status?.source ?? ''}`;
  const prevShown = useRef(shown);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (prevShown.current !== undefined && prevShown.current !== shown) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1200);
      prevShown.current = shown;
      return () => clearTimeout(t);
    }
    prevShown.current = shown;
    return undefined;
  }, [shown]);

  const dimCell = fids.dim ? 'opacity-55' : '';
  const disabledReason = selectMode ? selectDisabledReason(row) : null;
  const isSelected = selectMode ? selection!.selectedUrns.has(row.flightUrn) : false;
  const selectLabel = t('row.select', { flight: `${row.carrierCode}${row.flightNumber}` });

  return (
    <div
      className={cn(
        selectMode ? BOARD_GRID_SELECT : action ? BOARD_GRID_ACTION : BOARD_GRID,
        'group px-3 py-1.5 text-sm transition-colors hover:bg-muted/50',
        action && 'py-2',
        isSelected && 'bg-primary/5',
        pickMode &&
          'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      data-testid={`flight-row-${row.flightUrn}`}
      data-selected={isSelected ? 'true' : undefined}
      {...(onSelect
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: () => onSelect(row),
            onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(row);
              }
            },
          }
        : {})}
    >
      {selectMode ? (
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-primary disabled:cursor-not-allowed disabled:opacity-30"
          checked={isSelected}
          disabled={!!disabledReason}
          onChange={() => selection!.onToggle(row)}
          aria-label={selectLabel}
          title={disabledReason ? t(disabledReason) : selectLabel}
          data-testid={`flight-select-${row.flightUrn}`}
        />
      ) : null}
      {/* BOTH clocks (PO 2026-10-28): the board's own first and set big — the operator's
          «Hora local» by default, the departure airport's «Hora origen» (Issue #662) when
          switched — the other beside it, small (desktop). */}
      <span className={cn('font-mono text-[13px] font-semibold tabular-nums', dimCell)} data-testid="flight-time">
        {clock === 'origin' ? originTime : localTime}
      </span>
      <span
        className={cn('hidden font-mono text-[12px] tabular-nums text-muted-foreground sm:block', dimCell)}
        data-testid="flight-time-other"
      >
        {clock === 'origin' ? localTime : originTime}
      </span>
      <span className={cn('font-mono text-[13px] font-semibold tracking-wide', dimCell)}>
        {row.carrierCode}
        {row.flightNumber}
      </span>
      <span className={cn('flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5', dimCell)}>
        {/* Mono like the TIME/FLIGHT cells — a proportional face misaligns the
            XXX → ZZZ codes across rows. */}
        <span className="truncate font-mono text-[13px] font-medium uppercase tracking-wide">
          {row.origin && row.destination ? `${row.origin} → ${row.destination}` : '—'}
        </span>
        {row.disruptionUrn ? <DisruptionChip urn={row.disruptionUrn} link={!pickMode} /> : null}
      </span>
      {/* Spacer — pins the EQP/PAX/RISK/STATUS remarks block to the right edge. */}
      <span className="hidden sm:block" aria-hidden />
      <span
        className={cn(
          'hidden overflow-hidden whitespace-nowrap font-mono text-[11px] text-muted-foreground sm:block',
          dimCell,
        )}
      >
        {row.aircraftType ?? ''}
      </span>
      <span
        className={cn(
          'hidden font-mono text-xs tabular-nums text-muted-foreground sm:block',
          dimCell,
        )}
      >
        {typeof row.paxCount === 'number' && row.paxCount > 0 ? row.paxCount : ''}
      </span>
      <span
        className={cn('hidden font-mono text-xs tabular-nums sm:block', risk?.textClass)}
        title={riskTitle}
      >
        {risk && sources.risk ? (
          <>
            {/* Dotted underline = "hover for the source"; the name is also
                spoken, since a tooltip alone is invisible to a screen reader. */}
            <span
              className="cursor-help underline decoration-dotted underline-offset-2"
              data-testid="flight-risk-source"
              data-source={sources.risk.source}
            >
              {risk.text}
            </span>
            <span className="sr-only">
              {t('row.source.label', { source: t(sources.risk.labelKey) })}
            </span>
          </>
        ) : (
          (risk?.text ?? '')
        )}
      </span>
      <span className="flex min-w-0 flex-col items-start gap-0.5">
        <span
          className={cn(
            'font-mono text-[13px] font-bold uppercase tracking-wider',
            fids.textClass,
            flash && 'motion-safe:animate-pulse',
          )}
          data-testid="flight-status-cell"
        >
          {fids.labelParams ? t(fids.labelKey, fids.labelParams) : t(fids.labelKey)}
        </span>
        {etd ? (
          <span
            className="font-mono text-[10px] tabular-nums leading-none text-muted-foreground"
            data-testid="flight-status-etd"
          >
            {t('disruptions:delay.timeline.etd', {
              time: clock === 'origin' ? formatFlightClock24(etd, row.origin) : formatOperatorClock24(etd),
            })}
          </span>
        ) : null}
        {sources.status ? (
          <span
            className={cn(SOURCE_BADGE_CLASS, SOURCE_TONE[sources.status.source])}
            title={sourceTitle(sources.status)}
            data-testid="flight-status-source"
            data-source={sources.status.source}
          >
            {t(sources.status.labelKey)}
          </span>
        ) : null}
      </span>
      {action ? (
        <span className="col-span-full flex justify-start sm:col-span-1 sm:justify-end" data-testid="flight-row-action">
          {action}
        </span>
      ) : pickMode ? (
        <ChevronRight
          className="h-3.5 w-3.5 justify-self-end text-muted-foreground opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
          aria-hidden
        />
      ) : (
        <span aria-hidden />
      )}
    </div>
  );
}
